import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { Dialog, Image } from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import PropTypes from "prop-types";
import axios from "axios";
import PulseLoader from "react-spinners/PulseLoader";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import { renderPriceWithCurrency } from "@utils/utils";
import closeIcon from "@icons/ic-close.svg";
import errorIcon from "@icons/ic-error.svg";

import {
  SolutionTitleWrapper,
  SolutionTitle,
  AllocationSubTitle,
  CloseButton,
  SolutionButtonContainer,
  CancelButton,
  ConfirmButton,
  AllocationFooter,
  AllocationLoader,
  AllocationTableContainer,
  AllocationTable,
  AllocationTableHeaderRow,
  AllocationTableRow,
  AllocationTableData,
  EmptySolutionsMessage,
  AllocationErrorMessage,
} from "./SolutionsAllocationSettings.styled";

const SolutionsAllocationSettings = (props) => {
  const { t, onClose, onShowAllocationWarning, siteId, enquiryId } = props;

  const [validatedSolutions, setValidatedSolutions] = useState([]);
  const [isSolutionsLoading, setSolutionsLoadingStatus] = useState(false);
  const [isSubmitLoading, setSubmitLoading] = useState(false);
  const [sumOfAllocations, setSumOfAllocations] = useState(0);
  const [apiError, setApiError] = useState("");
  const generalParams = useGetGeneralParameters();

  const allocations = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

  const getPriceType = (code) => {
    switch (parseInt(code, 10)) {
      case 0:
        return "";
      case 1:
        return t("TREATMENT_AND_TRANSPORT_PRICE_SEPARATE");
      case 2:
        return t("TREATMENT_TRANSPORT_PRICE");
      case 3:
        return t("TRANSPORT_ONLY");
      case 4:
        return t("TREATMENT_ONLY");
      default:
    }
    return "";
  };

  const onAllocationChange = (e, id) => {
    const modifiedSolutions = validatedSolutions.map((solutionItem) => {
      if (solutionItem.id === id) {
        solutionItem.allocation = e.target.value;
      }
      return solutionItem;
    });
    let allocationSum = 0;
    for (const solutionItem of modifiedSolutions) {
      allocationSum += solutionItem.allocation
        ? parseInt(solutionItem.allocation, 10)
        : 0;
    }
    setSumOfAllocations(allocationSum);
    setApiError("");
    setValidatedSolutions(modifiedSolutions);
  };
  /**
   * On Submit send selling prices selected status.
   */
  const submitAllocation = () => {
    const modifiedAllocations = validatedSolutions.map((solutionItem) => {
      return {
        solutionId: solutionItem.id,
        allocation: solutionItem.allocation,
      };
    });
    setSubmitLoading(true);
    setApiError("");
    axios
      .post(`/enquiries/${enquiryId}/answeredByStudies`, modifiedAllocations)
      .then(() => {
        setApiError("");
        setSubmitLoading(false);
        onClose(true);
      })
      .catch((error) => {
        setSubmitLoading(false);
        setApiError(error.response.data.message);
      });
  };

  useEffect(() => {
    setSolutionsLoadingStatus(true);
    axios
      .get(
        `/solutions?limit=20&siteId=${siteId}&requestId=${enquiryId}&page=1&stage="${JSON.stringify(
          [7, 8, 9]
        )}"`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      )
      .then((response) => {
        let allocation = 0;
        const modifiedSolutions = response.data.solutions.map(
          (solutionItem) => {
            const tempData = { ...solutionItem };
            const selectedTranportPrice =
              tempData.solutionTransportPrices?.find(
                (transport) => transport.selectedPrice === true
              ) ?? null;
            tempData.supplierName = {
              name: tempData.centre?.supplierName
                ? tempData.centre.supplierName
                : tempData.haulier?.supplierName
                ? tempData.haulier.supplierName
                : tempData.linkedSolution?.suppilerName
                ? tempData.linkedSolution?.suppilerName
                : "",
              id: tempData.centre?.supplierId
                ? tempData.centre.supplierId
                : tempData.haulier?.supplierId
                ? tempData.haulier.supplierId
                : tempData.linkedSolution?.supplierId
                ? tempData.linkedSolution?.supplierId
                : "",
            };
            tempData.centreName = {
              name: tempData.centre?.companyName
                ? tempData.centre.companyName
                : tempData.haulier?.companyName
                ? tempData.haulier.companyName
                : tempData.linkedSolution?.companyName
                ? tempData.linkedSolution.companyName
                : "",
              id: tempData.centre?.companyId
                ? tempData.centre.companyId
                : tempData.haulier?.companyId
                ? tempData.haulier.companyId
                : tempData.linkedSolution?.companyId
                ? tempData.linkedSolution.companyId
                : "",
            };
            tempData.productName = tempData.centreProduct?.product?.name ?? "";
            tempData.treatmentPrice = {
              unit: tempData.solutionTreatmentPrices[0]?.unit
                ? t(tempData.solutionTreatmentPrices[0].unit)
                : "",
              price: tempData.solutionTreatmentPrices[0]?.price,
              currency: tempData.solutionTreatmentPrices[0]?.currency.id,
              position: tempData.solutionTreatmentPrices[0]?.currency.position,
            };

            tempData.transportPrice = {
              unit: selectedTranportPrice?.unit
                ? t(selectedTranportPrice?.unit)
                : "",
              price: selectedTranportPrice?.price,
              currency: selectedTranportPrice?.currency.id,
              position: selectedTranportPrice?.currency.position,
            };
            allocation += tempData.allocation
              ? parseInt(tempData.allocation, 10)
              : 0;
            return tempData;
          }
        );
        setSolutionsLoadingStatus(false);
        setSumOfAllocations(allocation);
        setValidatedSolutions(modifiedSolutions);
      })
      .catch((error) => {
        setValidatedSolutions([]);
        setSolutionsLoadingStatus(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog>
      <SolutionTitleWrapper>
        <SolutionTitle>
          {t("REQUEST_SOLUTIONS_VALIDATION_ALLOCATION_POP_UP_TITLE")}
        </SolutionTitle>
        <CloseButton onClick={() => onClose(false)}>
          <Image
            src={closeIcon}
            alt="close selling price popup"
            styles={{ width: "auto" }}
          />
        </CloseButton>
      </SolutionTitleWrapper>
      {isSolutionsLoading && (
        <AllocationLoader>
          <PulseLoader color="#004c7f" />
        </AllocationLoader>
      )}
      {validatedSolutions.length > 0 && !isSolutionsLoading && (
        <>
          <AllocationSubTitle>
            {t("REQUEST_SOLUTIONS_VALIDATION_ALLOCATION_POP_UP_SUBTITLE")}
          </AllocationSubTitle>
          <AllocationTableContainer>
            <AllocationTable>
              <thead>
                <AllocationTableHeaderRow>
                  <AllocationTableData>
                    {t("REQUEST_SOLUTIONS_VALIDATION_ALLOCATION_POP_UP_CENTER")}
                  </AllocationTableData>
                  <AllocationTableData>
                    {t(
                      "REQUEST_SOLUTIONS_VALIDATION_ALLOCATION_POP_UP_SUPPLIER"
                    )}
                  </AllocationTableData>
                  <AllocationTableData>
                    {t(
                      "REQUEST_SOLUTIONS_VALIDATION_ALLOCATION_POP_UP_PRODUCT"
                    )}
                  </AllocationTableData>
                  <AllocationTableData>
                    {t(
                      "REQUEST_SOLUTIONS_VALIDATION_ALLOCATION_POP_UP_PRICE_TYPE"
                    )}
                  </AllocationTableData>
                  <AllocationTableData>
                    {t(
                      "REQUEST_SOLUTIONS_VALIDATION_ALLOCATION_POP_UP_TREATMENT_PRICE"
                    )}
                  </AllocationTableData>
                  <AllocationTableData>
                    {t(
                      "REQUEST_SOLUTIONS_VALIDATION_ALLOCATION_POP_UP_TRANSPORT_PRICE"
                    )}
                  </AllocationTableData>
                  <AllocationTableData>
                    {t(
                      "REQUEST_SOLUTIONS_VALIDATION_ALLOCATION_POP_UP_ALLOCATION"
                    )}
                  </AllocationTableData>
                </AllocationTableHeaderRow>
              </thead>
              <tbody>
                {validatedSolutions.map((sellingPriceItem) => {
                  return (
                    <AllocationTableRow key={sellingPriceItem.id}>
                      <AllocationTableData>
                        {sellingPriceItem.centreName.name}
                      </AllocationTableData>
                      <AllocationTableData>
                        {sellingPriceItem.supplierName.name}
                      </AllocationTableData>
                      <AllocationTableData>
                        {sellingPriceItem.productName}
                      </AllocationTableData>
                      <AllocationTableData>
                        {getPriceType(sellingPriceItem.priceType)}
                      </AllocationTableData>
                      <AllocationTableData>
                        {(sellingPriceItem.treatmentPrice.price ||
                          parseInt(
                            sellingPriceItem.treatmentPrice?.price,
                            10
                          ) === 0) &&
                        generalParams?.countryCurrency
                          ? renderPriceWithCurrency(
                              sellingPriceItem.treatmentPrice.price,
                              sellingPriceItem.treatmentPrice.unit,
                              generalParams?.countryCurrency[
                                sellingPriceItem.treatmentPrice.currency
                              ],
                              sellingPriceItem.treatmentPrice.position ?? 1
                            )
                          : ""}
                      </AllocationTableData>
                      <AllocationTableData>
                        {(sellingPriceItem.transportPrice.price ||
                          parseInt(
                            sellingPriceItem.transportPrice?.price,
                            10
                          ) === 0) &&
                        generalParams?.countryCurrency
                          ? renderPriceWithCurrency(
                              sellingPriceItem.transportPrice.price,
                              sellingPriceItem.transportPrice.unit,
                              generalParams?.countryCurrency[
                                sellingPriceItem.transportPrice.currency
                              ],
                              sellingPriceItem.transportPrice.position ?? 1
                            )
                          : ""}
                      </AllocationTableData>
                      <AllocationTableData>
                        <select
                          value={sellingPriceItem.allocation ?? ""}
                          onChange={(e) =>
                            onAllocationChange(e, sellingPriceItem.id)
                          }
                        >
                          {allocations.map((allocation, index) => (
                            <option
                              value={allocation}
                              key={sellingPriceItem.id + index}
                            >
                              {`${allocation} %`}
                            </option>
                          ))}
                        </select>
                      </AllocationTableData>
                    </AllocationTableRow>
                  );
                })}
              </tbody>
            </AllocationTable>
          </AllocationTableContainer>
          <AllocationFooter>
            <AllocationErrorMessage>
              {sumOfAllocations !== 100 && (
                <Image
                  src={errorIcon}
                  alt="sum allocation is not 100%"
                  styles={{ width: "18px", marginRight: "6px" }}
                />
              )}
              {sumOfAllocations !== 100 && (
                <span>
                  {t(
                    "REQUEST_SOLUTIONS_VALIDATION_ALLOCATION_POP_UP_WARNING_MESSAGE"
                  )}
                </span>
              )}
              {apiError !== "" && <span>{apiError}</span>}
            </AllocationErrorMessage>
            <SolutionButtonContainer>
              <ConfirmButton
                onClick={submitAllocation}
                disabled={sumOfAllocations !== 100}
              >
                {!isSubmitLoading &&
                  t(
                    "REQUEST_SOLUTIONS_VALIDATION_ALLOCATION_POP_UP_SUBMIT_BUTTON"
                  )}
                {isSubmitLoading && <ClipLoader size={20} color="#fff" />}
              </ConfirmButton>
              <CancelButton onClick={onShowAllocationWarning}>
                {t(
                  "REQUEST_SOLUTIONS_VALIDATION_ALLOCATION_POP_UP_CANCEL_BUTTON"
                )}
              </CancelButton>
            </SolutionButtonContainer>
          </AllocationFooter>
        </>
      )}
      {validatedSolutions.length === 0 && !isSolutionsLoading && (
        <EmptySolutionsMessage>
          {t(
            "REQUEST_SOLUTIONS_VALIDATION_ALLOCATION_POP_UP_SUBMIT_NO_SOLUTION"
          )}
        </EmptySolutionsMessage>
      )}
    </Dialog>
  );
};

SolutionsAllocationSettings.propTypes = {
  t: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onShowAllocationWarning: PropTypes.func.isRequired,
  siteId: PropTypes.number,
  enquiryId: PropTypes.PropTypes,
};
SolutionsAllocationSettings.defaultProps = {
  siteId: null,
  enquiryId: null,
};

export default withTranslation()(SolutionsAllocationSettings);
